import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogIn, UserPlus, LogOut, UserCircle } from 'lucide-react';
import { Button } from '@caruso/ui';
import { useCart } from '@/contexts/cart-context';
import { useClientAuth } from '@/contexts/client-auth-context';
import { api } from '@/lib/axios';

interface StoreLayoutProps {
  children: React.ReactNode;
}

export function StoreLayout({ children }: StoreLayoutProps) {
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const { client, logout } = useClientAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [tenantName, setTenantName] = useState('Caruso Store');

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const response = await api.get('/settings/tenant-info');
        if (response.data?.name) {
          setTenantName(response.data.name);
        }
      } catch (error) {
        console.error('Erro ao buscar informações do tenant:', error);
      }
    };

    fetchTenantInfo();
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-brand-ink">
      {/* Header */}
      <header className="bg-brand-ink border-b border-brand-gold/20 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1
              className="text-2xl font-serif text-brand-gold cursor-pointer"
              onClick={() => navigate('/loja')}
            >
              {tenantName}
            </h1>

            <div className="flex items-center gap-2">
              {/* User Menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <User className="w-5 h-5" />
                </Button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsUserMenuOpen(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-64 bg-brand-ink border border-brand-gold/20 rounded-lg shadow-lg z-50 overflow-hidden">
                      {client ? (
                        // Logged in state
                        <>
                          <div className="p-4 border-b border-brand-gold/20 bg-brand-gold/5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center">
                                <UserCircle className="w-6 h-6 text-brand-gold" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-brand-ivory truncate">
                                  {client.name}
                                </p>
                                <p className="text-xs text-brand-ivory/60 truncate">
                                  {client.email}
                                </p>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => {
                              navigate('/client/dashboard');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-sm text-brand-ivory hover:bg-brand-gold/10 transition-colors flex items-center gap-3"
                          >
                            <UserCircle className="w-4 h-4" />
                            Minha Conta
                          </button>

                          <button
                            onClick={handleLogout}
                            className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-3 border-t border-brand-gold/20"
                          >
                            <LogOut className="w-4 h-4" />
                            Sair
                          </button>
                        </>
                      ) : (
                        // Not logged in state
                        <>
                          <div className="p-4 border-b border-brand-gold/20">
                            <p className="text-sm text-brand-ivory/80">
                              Entre ou crie uma conta
                            </p>
                          </div>

                          <button
                            onClick={() => {
                              navigate('/client/login');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-sm text-brand-ivory hover:bg-brand-gold/10 transition-colors flex items-center gap-3"
                          >
                            <LogIn className="w-4 h-4" />
                            Fazer Login
                          </button>

                          <button
                            onClick={() => {
                              navigate('/client/register');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-sm text-brand-gold hover:bg-brand-gold/10 transition-colors flex items-center gap-3"
                          >
                            <UserPlus className="w-4 h-4" />
                            Criar Conta
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Shopping Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => navigate('/loja/carrinho')}
              >
                <ShoppingCart className="w-5 h-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-gold text-brand-dark text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {totalItems}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
